// src/components/Home.js
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "../context/LanguageContext";
import Counter from "../utils/counter";

import {
  ArrowUpRight,
  Sparkles,
  Cpu,
  ShieldCheck,
  Leaf,
  Mail,
} from "lucide-react";

import "./Home.css";

const Home = () => {
  const { translate } = useLanguage();

  const t = (key, fallback) => {
    const translation = translate(key);

    if (!translation || translation === key || translation === "") {
      return fallback;
    }

    return translation;
  };

  const [currentTextIndex, setCurrentTextIndex] = useState(0);

  const heroTexts = [
    {
      key: "heroText1",
      fallback: "Innovamos en cada pozo, transformamos la industria",
    },
    {
      key: "heroText2",
      fallback: "Energía, ciencia y sostenibilidad en cada solución",
    },
    {
      key: "heroText3",
      fallback: "Tecnología y eficiencia para operaciones responsables",
    },
  ];

  useEffect(() => {
    const textTimer = setInterval(() => {
      setCurrentTextIndex((prev) => (prev + 1) % heroTexts.length);
    }, 5000);

    return () => clearInterval(textTimer);
  }, []);

  return (
    <div className="w-full overflow-hidden">
      {/* HERO */}
      <section
        className="
          relative
          w-full
          h-[78vh]
          min-h-[560px]
          max-h-[760px]
        "
      >
        <video
          autoPlay
          muted
          loop
          playsInline
          className="
            absolute
            inset-0
            w-full
            h-full
            object-cover
          "
        >
          <source src="/Images/home/banner001_gsa.mp4" type="video/mp4" />
        </video>

        <div
          className="
            absolute
            inset-0
            bg-black/30
          "
        />

        <div
          className="
            absolute
            inset-0
            bg-gradient-to-r
            from-black/45
            via-black/10
            to-transparent
          "
        />

        <div
          className="
            relative
            z-10
            h-full
            max-w-[1400px]
            mx-auto
            px-[7%]
            flex
            items-center
          "
        >
          <div
            className="
              max-w-[700px]
              pt-10
            "
          >
            <h1
              key={currentTextIndex}
              className="
                text-white
                font-[800]
                leading-[1.05]
                text-[34px]
                sm:text-[46px]
                md:text-[58px]
                lg:text-[66px]
                drop-shadow-[0_2px_8px_rgba(0,0,0,0.35)]
                transition-all
                duration-500
              "
            >
              {t(
                heroTexts[currentTextIndex].key,
                heroTexts[currentTextIndex].fallback,
              )}
            </h1>

            <p
              className="
                mt-5
                text-white/85
                text-[15px]
                sm:text-[16px]
                leading-[1.8]
                max-w-[580px]
              "
            >
              Soluciones especializadas para la industria energética con enfoque
              en eficiencia, sostenibilidad e innovación.
            </p>

            <div
              className="
                flex
                flex-col
                sm:flex-row
                gap-4
                mt-8
              "
            >
              <a
                href="#servicios"
                className="
    h-[52px]
    px-7
    rounded-lg
    bg-[#ee7d32]
    hover:bg-[#ee7d32]
    text-white
    font-semibold
    text-[14px]
    flex
    items-center
    justify-center
    transition-all
    duration-300
  "
              >
                Nuestros Servicios
              </a>

              <Link
                to="/contact-us"
                className="
                  h-[52px]
                  px-7
                  rounded-lg
                  bg-white/10
                  hover:bg-white/20
                  border
                  border-white/15
                  backdrop-blur-sm
                  text-white
                  font-semibold
                  text-[14px]
                  flex
                  items-center
                  justify-center
                  transition-all
                  duration-300
                "
              >
                Contáctanos
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* SOBRE NOSOTROS */}
      <section className="relative bg-[#f5f5f5] py-16 lg:py-24 overflow-hidden">
        <div
          className="
      absolute
      top-[-120px]
      right-[-100px]
      w-[320px]
      h-[320px]
      rounded-full
      bg-[#ee7d32]/10
      blur-3xl
    "
        />

        <div
          className="
      max-w-[1350px]
      mx-auto
      px-6
      sm:px-10
      lg:px-16
    "
        >
          <div
            className="
        grid
        grid-cols-1
        lg:grid-cols-2
        gap-14
        lg:gap-20
        items-center
      "
          >
            <div className="order-2 lg:order-1">
              <span
                className="
            text-[#ee7d32]
            font-[700]
            text-[15px]
            sm:text-[16px]
            block
            mb-2
          "
              >
                SOBRE NOSOTROS
              </span>

              <h2
                className="
            text-[#111]
            font-[800]
            leading-[1.05]
            text-[34px]
            sm:text-[42px]
            lg:text-[54px]
            max-w-[620px]
          "
              >
                Quiénes Somos
              </h2>

              <p
                className="
            mt-5
            text-gray-700
            text-[15px]
            sm:text-[16px]
            leading-[1.9]
            max-w-[620px]
          "
              >
                Sierra Alta SAS es una empresa colombiana con amplia experiencia
                en la prestación de servicios especializados en Fluidos de
                Perforación, Fluidos de Completamiento y Control de Sólidos,
                además de contar con un Laboratorio de Análisis Ambiental
                acreditado por el IDEAM.
              </p>

              <div
                className="
    mt-10
    grid
    grid-cols-1
    sm:grid-cols-2
    lg:grid-cols-3
    gap-6
  "
              >
                <div
                  className="
      flex
      items-center
      gap-4
      rounded-2xl
      border
      border-gray-100
      p-4
      transition-all
      duration-300
      hover:-translate-y-1
    "
                >
                  <img
                    src="/Images/home/configuraciones.webp"
                    alt="Experiencia"
                    className="
        w-[56px]
        h-[56px]
        rounded-2xl
        object-cover
        flex-shrink-0
      "
                  />

                  <div>
                    <h3
                      className="
          text-[#111]
          font-[800]
          text-[20px]
          leading-none
        "
                    >
                      10+
                    </h3>

                    <p
                      className="
          text-gray-500
          text-[13px]
          mt-1
          leading-[1.4]
        "
                    >
                      Años de experiencia
                    </p>
                  </div>
                </div>

                <div
                  className="
      flex
      items-center
      gap-4
      rounded-2xl
      border
      border-gray-100
      p-4
      transition-all
      duration-300
      hover:-translate-y-1
    "
                >
                  <img
                    src="/Images/home/seguridad.webp"
                    alt="Calidad"
                    className="
        w-[56px]
        h-[56px]
        rounded-2xl
        object-cover
        flex-shrink-0
      "
                  />

                  <div>
                    <h3
                      className="
          text-[#111]
          font-[800]
          text-[20px]
          leading-none
        "
                    >
                      100%
                    </h3>

                    <p
                      className="
          text-gray-500
          text-[13px]
          mt-1
          leading-[1.4]
        "
                    >
                      Calidad garantizada
                    </p>
                  </div>
                </div>

                <div
                  className="
      flex
      items-center
      gap-4
      rounded-2xl
      border
      border-gray-100
      p-4
      transition-all
      duration-300
      hover:-translate-y-1
    "
                >
                  <img
                    src="/Images/home/save.webp"
                    alt="Ambiental"
                    className="
        w-[56px]
        h-[56px]
        rounded-2xl
        object-cover
        flex-shrink-0
      "
                  />

                  <div>
                    <h3
                      className="
          text-[#111]
          font-[800]
          text-[17px]
          leading-none
        "
                    >
                      Compromiso
                    </h3>

                    <p
                      className="
          text-gray-500
          text-[13px]
          mt-1
          leading-[1.4]
        "
                    >
                      Sostenibilidad ambiental
                    </p>
                  </div>
                </div>
              </div>
              <div className="mt-8">
                <Link
                  to="/company"
                  className="
      group
      inline-flex
      items-center
      gap-3
      text-[#111]
      font-[700]
      text-[16px]
      transition-all
      duration-300
    "
                >
                  <span
                    className="
        relative
        after:absolute
        after:left-0
        after:-bottom-1
        after:h-[2px]
        after:w-0
        after:bg-[#ee7d32]
        after:transition-all
        after:duration-300
        group-hover:after:w-full
        group-hover:text-[#ee7d32]
        group-hover:after:w-full
      "
                  >
                    Conoce más
                  </span>

                  <div
                    className="
    w-10
    h-10
    rounded-full
    bg-[#ee7d32]
    flex
    items-center
    justify-center
    transition-all
    duration-300
    group-hover:translate-x-1
    group-hover:scale-110
  "
                  >
                    <ArrowUpRight
                      size={18}
                      strokeWidth={2.5}
                      className="
      stroke-black
      group-hover:stroke-white
      transition-all
      duration-300
    "
                    />
                  </div>
                </Link>
              </div>
            </div>

            <div
              className="
          relative
          order-1
          lg:order-2
        "
            >
              <div
                className="
            relative
            overflow-hidden
            rounded-[28px]
            border-[3px]
            border-[#ee7d32]
            shadow-[0_20px_60px_rgba(0,0,0,0.12)]
          "
              >
                <img
                  src="/Images/home/sobre_nosotros.webp"
                  alt="Sierra Alta"
                  className="
              w-full
              h-[320px]
              sm:h-[420px]
              lg:h-[500px]
              object-cover
              hover:scale-105
              transition-all
              duration-700
            "
                />

                <div
                  className="
              absolute
              inset-0
              bg-gradient-to-t
              from-black/20
              to-transparent
            "
                />
              </div>

              <div
                className="
    hidden
    md:flex
    absolute
    bottom-[-25px]
    left-[-25px]
    bg-white
    rounded-2xl
    px-6
    py-5
    shadow-[0_15px_40px_rgba(0,0,0,0.12)]
    items-center
    gap-4
  "
              >
                <div
                  className="
      w-14
      h-14
      rounded-full
      bg-[#ee7d32]
      flex
      items-center
      justify-center
      overflow-hidden
      flex-shrink-0
    "
                >
                  <img
                    src="/Images/home/plataform.webp"
                    alt="Award"
                    className="
        w-7
        h-7
        object-contain
      "
                  />
                </div>

                <div>
                  <h4
                    className="
        text-[#111]
        font-[800]
        text-[18px]
      "
                  >
                    Líderes del sector
                  </h4>

                  <p
                    className="
        text-gray-500
        text-[13px]
        mt-1
        leading-[1.5]
      "
                  >
                    Servicios especializados de alto nivel
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* NUESTROS SERVICIOS */}
      <section
        id="servicios"
        className="relative bg-[white] py-20 lg:py-28 overflow-hidden"
      >
        <div
          className="
      max-w-[1380px]
      mx-auto
      px-6
      sm:px-10
      lg:px-16
    "
        >
          <div
            className="
        flex
        flex-col
        lg:flex-row
        lg:items-end
        lg:justify-between
        gap-8
      "
          >
            <div className="max-w-[760px]">
              <span
                className="
            text-[#ee7d32]
            font-[700]
            text-[14px]
            tracking-[2px]
            block
            mb-3
          "
              >
                QUÉ HACEMOS
              </span>

              <h2
                className="
            text-[#111]
            font-[800]
            leading-[1.05]
            text-[36px]
            sm:text-[48px]
            lg:text-[60px]
          "
              >
                Soluciones técnicas en energía y medio ambiente
              </h2>
            </div>

            <Link
              to="/services"
              className="
          group
          inline-flex
          items-center
          gap-3
          text-[#111]
          font-[700]
          text-[16px]
          transition-all
          duration-300
          shrink-0
        "
            >
              <span
                className="
            relative
            after:absolute
            after:left-0
            after:-bottom-1
            after:h-[2px]
            after:w-0
            after:bg-[#ee7d32]
            after:transition-all
            after:duration-300
            group-hover:after:w-full
            group-hover:text-[#ee7d32]
          "
              >
                Explorar Servicios
              </span>

              <div
                className="
            w-11
            h-11
            rounded-full
            bg-[#ee7d32]
            flex
            items-center
            justify-center
            transition-all
            duration-300
            group-hover:translate-x-1
            group-hover:scale-110
          "
              >
                <ArrowUpRight
                  size={18}
                  strokeWidth={2.5}
                  className="
              stroke-black
              group-hover:stroke-white
              transition-all
              duration-300
            "
                />
              </div>
            </Link>
          </div>

          {/* SERVICES */}
          <div
            className="
        mt-16
        grid
        grid-cols-1
        xl:grid-cols-2
        gap-8
      "
          >
            {/* OIL & GAS */}
            <div
              className="
      group
      relative
      overflow-hidden
      rounded-[38px]
      bg-white
      border border-black/5
      shadow-[0_20px_60px_rgba(0,0,0,0.06)]
      transition-all
      duration-500
      hover:-translate-y-1
      hover:shadow-[0_30px_80px_rgba(0,0,0,0.10)]
    "
            >
              <div className="relative h-[340px] overflow-hidden">
                <img
                  src="/Images/home/oilgas.webp"
                  alt="Oil & Gas"
                  className="
          w-full
          h-full
          object-cover
          transition-all
          duration-[1800ms]
          group-hover:scale-110
        "
                />

                <div
                  className="
          absolute
          inset-0
          bg-gradient-to-t
          from-black/80
          via-black/20
          to-transparent
        "
                />

                <div
                  className="
          absolute
          top-[-120px]
          right-[-100px]
          w-[260px]
          h-[260px]
          rounded-full
          bg-[#ee7d32]/30
          blur-[100px]
        "
                />

                <div className="absolute bottom-0 left-0 p-8 sm:p-10">
                  <div
                    className="
            inline-flex
            items-center
            gap-4
            rounded-full
            bg-white/10
            backdrop-blur-xl
            border border-white/10
            px-5
            py-3
          "
                  >
                    <div
                      className="
              w-12
              h-12
              rounded-full
              bg-[#ee7d32]
              flex
              items-center
              justify-center
              shadow-lg
            "
                    >
                      <img
                        src="/Images/home/plataform.webp"
                        alt="Plataform"
                        className="w-6 h-6 object-contain"
                      />
                    </div>

                    <div>
                      <p
                        className="
                text-white/60
                text-[11px]
                uppercase
                tracking-[2px]
                font-[700]
              "
                      >
                        División
                      </p>

                      <p
                        className="
                text-white
                font-[800]
                text-[20px]
              "
                      >
                        Oil & Gas
                      </p>
                    </div>
                  </div>

                  <h3
                    className="
            mt-7
            text-white
            font-[800]
            leading-[1]
            text-[34px]
            sm:text-[46px]
            max-w-[620px]
          "
                  >
                    Fluidos, control y optimización operativa
                  </h3>
                </div>
              </div>

              <div className="p-8 sm:p-10">
                <p
                  className="
          text-gray-600
          text-[15px]
          leading-[2]
        "
                >
                  Soluciones especializadas para perforación y completamiento
                  enfocadas en rendimiento, seguridad y continuidad operacional.
                </p>

                {/* SERVICES */}
                <div
                  className="
          mt-10
          grid
          grid-cols-1
          sm:grid-cols-2
          gap-x-8
          gap-y-5
        "
                >
                  {[
                    "Fluidos de perforación",
                    "Fluidos de completamiento",
                    "Control de sólidos",
                    "Consultoría y optimización",
                    "Productos químicos",
                  ].map((item, index) => (
                    <div
                      key={index}
                      className="
              flex
              items-center
              gap-4
            "
                    >
                      <div
                        className="
                w-10
                h-[2px]
                bg-[#ee7d32]
              "
                      />

                      <p
                        className="
                text-[#111]
                text-[15px]
                font-[600]
              "
                      >
                        {item}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* AMBIENTAL */}
            <div
              className="
      group
      relative
      overflow-hidden
      rounded-[38px]
      bg-white
      border border-black/5
      shadow-[0_20px_60px_rgba(0,0,0,0.06)]
      transition-all
      duration-500
      hover:-translate-y-1
      hover:shadow-[0_30px_80px_rgba(0,0,0,0.10)]
    "
            >
              <div className="relative h-[340px] overflow-hidden">
                <img
                  src="/Images/home/labamb_011.webp"
                  alt="Ambiental"
                  className="
          w-full
          h-full
          object-cover
          transition-all
          duration-[1800ms]
          group-hover:scale-110
        "
                />

                <div
                  className="
          absolute
          inset-0
          bg-gradient-to-t
          from-black/75
          via-black/15
          to-transparent
        "
                />

                <div
                  className="
          absolute
          top-[-120px]
          right-[-100px]
          w-[260px]
          h-[260px]
          rounded-full
          bg-[#3b8d3d]/30
          blur-[100px]
        "
                />

                <div className="absolute bottom-0 left-0 p-8 sm:p-10">
                  <div
                    className="
            inline-flex
            items-center
            gap-4
            rounded-full
            bg-white/10
            backdrop-blur-xl
            border border-white/10
            px-5
            py-3
          "
                  >
                    <div
                      className="
              w-12
              h-12
              rounded-full
              bg-[#3b8d3d]
              flex
              items-center
              justify-center
              shadow-lg
            "
                    >
                      <img
                        src="/Images/home/analisis-ambiental.webp"
                        alt=""
                        className="w-6 h-6 object-contain"
                      />
                    </div>

                    <div>
                      <p
                        className="
                text-white/60
                text-[11px]
                uppercase
                tracking-[2px]
                font-[700]
              "
                      >
                        División
                      </p>

                      <p
                        className="
                text-white
                font-[800]
                text-[20px]
              "
                      >
                        Gestión Ambiental
                      </p>
                    </div>
                  </div>

                  <h3
                    className="
            mt-7
            text-white
            font-[800]
            leading-[1]
            text-[34px]
            sm:text-[46px]
            max-w-[620px]
          "
                  >
                    Monitoreo, laboratorio y sostenibilidad
                  </h3>
                </div>
              </div>

              <div className="p-8 sm:p-10">
                <p
                  className="
          text-gray-600
          text-[15px]
          leading-[2]
        "
                >
                  Servicios ambientales integrales orientados al monitoreo
                  técnico, cumplimiento normativo y sostenibilidad operativa.
                </p>

                {/* SERVICES */}
                <div
                  className="
          mt-10
          grid
          grid-cols-1
          sm:grid-cols-2
          gap-x-8
          gap-y-5
        "
                >
                  {[
                    "Monitoreo ambiental",
                    "Análisis de laboratorio",
                    "Consultoría ambiental",
                    "Descarbonización",
                    "Alquiler de equipos",
                  ].map((item, index) => (
                    <div
                      key={index}
                      className="
              flex
              items-center
              gap-4
            "
                    >
                      <div
                        className="
                w-10
                h-[2px]
                bg-[#3b8d3d]
              "
                      />

                      <p
                        className="
                text-[#111]
                text-[15px]
                font-[600]
              "
                      >
                        {item}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* OPERACIONES */}
      <section className="relative bg-[#6f6f6f] py-20 lg:py-28 overflow-hidden">
        <img
          src="/Images/home/plataform.webp"
          alt="plataform"
          className="
      hidden
      xl:block
      absolute
      right-[-80px]
      bottom-[-40px]
      w-[340px]
      opacity-[0.06]
      pointer-events-none
      select-none
    "
        />

        <div
          className="
      max-w-[1380px]
      mx-auto
      px-6
      sm:px-10
      lg:px-16
      relative
      z-10
    "
        >
          <div
            className="
        grid
        grid-cols-1
        lg:grid-cols-[1fr_1fr]
        gap-14
        xl:gap-20
        items-center
      "
          >
            <div className="relative order-2 lg:order-1">
              <div
                className="
            relative
            overflow-hidden
            rounded-[38px]
            shadow-[0_25px_60px_rgba(0,0,0,0.18)]
          "
              >
                <img
                  src="/Images/home/DW.webp"
                  alt="Operaciones"
                  className="
              w-full
              h-[320px]
              sm:h-[420px]
              lg:h-[500px]
              object-cover
              transition-transform
              duration-[1800ms]
              hover:scale-105
            "
                />

                <div
                  className="
              absolute
              inset-0
              bg-gradient-to-t
              from-black/25
              via-transparent
              to-transparent
            "
                />
              </div>
            </div>

            <div
              className="
          relative
          order-1
          lg:order-2
          pl-2
          lg:pl-4
          xl:pl-8
        "
            >
              <span
                className="
            text-[#ee7d32]
            font-[700]
            text-[15px]
            tracking-[2px]
            uppercase
          "
              >
                Operaciones
              </span>

              <h2
                className="
            mt-4
            text-white
            font-[800]
            leading-[1.05]
            text-[38px]
            sm:text-[52px]
            lg:text-[64px]
            tracking-[-2px]
          "
              >
                Ejecución con precisión
                <br />y altos <span className="text-[#ee7d32]">estándares</span>
              </h2>

              <p
                className="
            mt-8
            text-white/75
            text-[16px]
            sm:text-[18px]
            leading-[1.9]
            max-w-[520px]
          "
              >
                Contamos con personal altamente calificado, equipos
                especializados y procedimientos robustos que nos permiten
                adaptarnos a los desafíos de cada proyecto.
              </p>

              <div className="mt-12">
                <Link
                  to="/operations"
                  className="
    group
    inline-flex
    items-center
    gap-3
    font-[700]
    text-[16px]
    transition-all
    duration-300
    shrink-0
  "
                >
                  <span
                    className="
      relative
      text-white/70
      group-hover:text-white
      transition-all
      duration-300
      after:absolute
      after:left-0
      after:-bottom-1
      after:h-[2px]
      after:w-0
      after:bg-[#ee7d32]
      after:transition-all
      after:duration-300
      group-hover:after:w-full
    "
                  >
                    Ver Operaciones
                  </span>

                  <div
                    className="
      w-11
      h-11
      rounded-full
      bg-[#ee7d32]
      flex
      items-center
      justify-center
      transition-all
      duration-300
      group-hover:translate-x-1
      group-hover:scale-110
    "
                  >
                    <ArrowUpRight
                      size={18}
                      strokeWidth={2.5}
                      className="
        stroke-black
        group-hover:stroke-white
        transition-all
        duration-300
      "
                    />
                  </div>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SOSTENIBILIDAD */}
      <section className="relative bg-[#f5f5f5] py-16 lg:py-24 overflow-hidden">
        <div
          className="
      absolute
      top-[-120px]
      right-[-100px]
      w-[320px]
      h-[320px]
      rounded-full
      bg-[#6FAF4B]/10
      blur-3xl
    "
        />

        <div
          className="
      max-w-[1350px]
      mx-auto
      px-6
      sm:px-10
      lg:px-16
    "
        >
          <div
            className="
        grid
        grid-cols-1
        lg:grid-cols-2
        gap-14
        lg:gap-20
        items-center
      "
          >
            <div className="order-1">
              <span
                className="
            text-[#ee7d32]
            font-[700]
            text-[15px]
            sm:text-[16px]
            block
            mb-2
          "
              >
                SOSTENIBILIDAD
              </span>
              <h2
                className="
            mt-4
            text-[#111111]
            font-[800]
            leading-[1.05]
            text-[38px]
            sm:text-[52px]
            lg:text-[64px]
            tracking-[-2px]
          "
              >
                Comprometidos con el desarrollo{" "}
                <span className="text-[#ee7d32]">sostenible</span>
              </h2>

              <p
                className="
            mt-5
            text-gray-700
            text-[15px]
            sm:text-[16px]
            leading-[1.9]
            max-w-[620px]
          "
              >
                Implementamos prácticas responsables orientadas a la protección
                ambiental, el uso eficiente de los recursos y el cumplimiento
                normativo en todas nuestras operaciones.
              </p>

              <div className="mt-8">
                <Link
                  to="/sustainability"
                  className="
              group
              inline-flex
              items-center
              gap-3
              text-[#111]
              font-[700]
              text-[16px]
              transition-all
              duration-300
            "
                >
                  <span
                    className="
                relative
                after:absolute
                after:left-0
                after:-bottom-1
                after:h-[2px]
                after:w-0
                after:bg-[#ee7d32]
                after:transition-all
                after:duration-300
                group-hover:after:w-full
                group-hover:text-[#ee7d32]
              "
                  >
                    Conoce nuestras iniciativas
                  </span>

                  <div
                    className="
                w-10
                h-10
                rounded-full
                bg-[#ee7d32]
                flex
                items-center
                justify-center
                transition-all
                duration-300
                group-hover:translate-x-1
                group-hover:scale-110
              "
                  >
                    <ArrowUpRight
                      size={18}
                      strokeWidth={2.5}
                      className="
                  stroke-black
                  group-hover:stroke-white
                  transition-all
                  duration-300
                "
                    />
                  </div>
                </Link>
              </div>
            </div>

            <div
              className="
          relative
          order-2
        "
            >
              <div
                className="
            relative
            overflow-hidden
            rounded-[28px]
            border-[3px]
            border-[#6FAF4B]/20
            shadow-[0_20px_60px_rgba(0,0,0,0.12)]
          "
              >
                <img
                  src="/Images/home/sostenibilidad.webp"
                  alt="Sierra Alta"
                  className="
              w-full
              h-[320px]
              sm:h-[420px]
              lg:h-[500px]
              object-cover
              hover:scale-105
              transition-all
              duration-700
            "
                />

                <div
                  className="
              absolute
              inset-0
              bg-gradient-to-t
              from-black/20
              to-transparent
            "
                />
              </div>

              <div
                className="
            hidden
            md:flex
            absolute
            bottom-[-25px]
            left-[-25px]
            bg-white/95
            backdrop-blur-xl
            rounded-2xl
            px-6
            py-5
            shadow-[0_15px_40px_rgba(0,0,0,0.12)]
            items-center
            gap-4
            border
            border-white
          "
              >
                <div
                  className="
              w-14
              h-14
              rounded-2xl
              bg-[#6FAF4B]/10
              flex
              items-center
              justify-center
              overflow-hidden
              flex-shrink-0
            "
                >
                  <Leaf
                    size={28}
                    strokeWidth={2.2}
                    className="text-[#6FAF4B]"
                  />
                </div>

                <div>
                  <h4
                    className="
                text-[#111]
                font-[800]
                text-[18px]
                leading-tight
              "
                  >
                    Operación responsable
                  </h4>

                  <p
                    className="
                text-gray-500
                text-[13px]
                mt-1
                leading-[1.5]
              "
                  >
                    Prácticas sostenibles enfocadas en el cuidado ambiental.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TECNOLOGÍA E INNOVACIÓN */}
      <section className="relative bg-white py-16 lg:py-24 overflow-hidden">
        <div
          className="
      absolute
      bottom-[-120px]
      left-[-100px]
      w-[320px]
      h-[320px]
      rounded-full
      bg-[#ee7d32]/10
      blur-3xl
    "
        />

        <div
          className="
      max-w-[1350px]
      mx-auto
      px-6
      sm:px-10
      lg:px-16
      relative
      z-10
    "
        >
          <div
            className="
        grid
        grid-cols-1
        lg:grid-cols-2
        gap-14
        lg:gap-20
        items-center
      "
          >
            <div className="relative order-2 lg:order-1">
              <div
                className="
            relative
            overflow-hidden
            rounded-[30px]
            shadow-[0_20px_60px_rgba(0,0,0,0.12)]
          "
              >
                <img
                  src="/Images/home/tecnologia.webp"
                  alt="Tecnología Sierra Alta"
                  className="
              w-full
              h-[320px]
              sm:h-[420px]
              lg:h-[540px]
              object-cover
              hover:scale-105
              transition-all
              duration-700
            "
                />

                <div
                  className="
              absolute
              inset-0
              bg-gradient-to-t
              from-black/20
              to-transparent
            "
                />
              </div>

              <div
                className="
            hidden
            md:flex
            absolute
            top-[-20px]
            right-[-20px]
            bg-white
            rounded-2xl
            px-5
            py-4
            shadow-[0_15px_40px_rgba(243,112,33,0.15)]
            items-center
            gap-4
            border
            border-[#ee7d32]/10
          "
              >
                <div
                  className="
              w-12
              h-12
              rounded-2xl
              bg-[#ee7d32]
              flex
              items-center
              justify-center
              shadow-[0_10px_25px_rgba(243,112,33,0.25)]
            "
                >
                  <Sparkles
                    size={22}
                    className="stroke-white"
                    strokeWidth={2.3}
                  />
                </div>

                <div>
                  <h4
                    className="
                text-[#111]
                font-[800]
                text-[16px]
              "
                  >
                    Innovación aplicada
                  </h4>

                  <p
                    className="
                text-gray-500
                text-[13px]
                mt-1
              "
                  >
                    Tecnología eficiente y sostenible.
                  </p>
                </div>
              </div>
            </div>

            <div className="order-1 lg:order-2">
              <span
                className="
            text-[#ee7d32]
            font-[700]
            text-[15px]
            sm:text-[16px]
            block
            mb-3
          "
              >
                TECNOLOGÍA E INNOVACIÓN
              </span>

              <h2
                className="
            text-[#111]
            font-[800]
            leading-[1.05]
            text-[38px]
            sm:text-[52px]
            lg:text-[60px]
            tracking-[-2px]
            max-w-[650px]
          "
              >
                Soluciones tecnológicas para una industria eficiente
              </h2>

              <p
                className="
            mt-6
            text-gray-600
            text-[16px]
            sm:text-[17px]
            leading-[1.9]
            max-w-[620px]
          "
              >
                En Sierra Alta SAS desarrollamos tecnologías avanzadas para
                optimizar procesos energéticos, garantizando operaciones más
                eficientes, seguras y sostenibles.
              </p>

              <div
                className="
            mt-10
            grid
            grid-cols-1
            sm:grid-cols-2
            gap-5
          "
              >
                <div
                  className="
              flex
              items-start
              gap-4
              p-5
              rounded-2xl
              bg-white
              border
              border-[#ee7d32]/10
              shadow-[0_10px_30px_rgba(243,112,33,0.06)]
              transition-all
              duration-300
              hover:-translate-y-1
              hover:shadow-[0_20px_40px_rgba(243,112,33,0.12)]
            "
                >
                  <div
                    className="
                min-w-[54px]
                h-[54px]
                rounded-2xl
                bg-[#ee7d32]
                flex
                items-center
                justify-center
                shadow-[0_10px_25px_rgba(243,112,33,0.25)]
              "
                  >
                    <Cpu size={25} className="stroke-white" strokeWidth={2.3} />
                  </div>

                  <div>
                    <h3
                      className="
                  text-[#111]
                  font-[800]
                  text-[17px]
                "
                    >
                      Optimización
                    </h3>

                    <p
                      className="
                  mt-2
                  text-gray-500
                  text-[14px]
                  leading-[1.7]
                "
                    >
                      Procesos más ágiles y eficientes.
                    </p>
                  </div>
                </div>

                <div
                  className="
              flex
              items-start
              gap-4
              p-5
              rounded-2xl
              bg-white
              border
              border-[#ee7d32]/10
              shadow-[0_10px_30px_rgba(243,112,33,0.06)]
              transition-all
              duration-300
              hover:-translate-y-1
              hover:shadow-[0_20px_40px_rgba(243,112,33,0.12)]
            "
                >
                  <div
                    className="
                min-w-[54px]
                h-[54px]
                rounded-2xl
                bg-[#ee7d32]
                flex
                items-center
                justify-center
                shadow-[0_10px_25px_rgba(243,112,33,0.25)]
              "
                  >
                    <ShieldCheck
                      size={25}
                      className="stroke-white"
                      strokeWidth={2.3}
                    />
                  </div>

                  <div>
                    <h3
                      className="
                  text-[#111]
                  font-[800]
                  text-[17px]
                "
                    >
                      Seguridad
                    </h3>

                    <p
                      className="
                  mt-2
                  text-gray-500
                  text-[14px]
                  leading-[1.7]
                "
                    >
                      Operaciones seguras y confiables.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECCION */}
      <section className="relative bg-[#f5f5f5] py-14 overflow-hidden">
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] rounded-full bg-[#ee7d32]/5 blur-3xl" />

        <div className="relative z-10 max-w-[1350px] mx-auto px-6 sm:px-10 lg:px-16">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-y-10 lg:gap-0">
            <div className="relative flex flex-col items-center text-center group">
              <div className="w-16 h-16 rounded-2xl flex items-center justify-center transition-all duration-300 group-hover:-translate-y-1 overflow-hidden">
                <img
                  src="/Images/home/configuraciones.webp"
                  alt="Experiencia"
                  className="w-14 h-14 object-contain"
                />
              </div>

              <h3 className="mt-5 text-[#111] font-[800] text-[28px] leading-none">
                <Counter end={10} prefix="+" />
              </h3>

              <p className="mt-2 text-gray-500 text-[14px] leading-[1.5] max-w-[150px]">
                Años de experiencia
              </p>

              <div className="hidden lg:block absolute right-0 top-1/2 -translate-y-1/2 w-[1px] h-[80px] bg-gradient-to-b from-transparent via-gray-300 to-transparent" />
            </div>

            <div className="relative flex flex-col items-center text-center group">
              <div className="w-16 h-16 rounded-2xl flex items-center justify-center transition-all duration-300 group-hover:-translate-y-1 overflow-hidden">
                <img
                  src="/Images/home/pozos.webp"
                  alt="Pozos"
                  className="w-14 h-14 object-contain"
                />
              </div>

              <h3 className="mt-5 text-[#111] font-[800] text-[28px] leading-none">
                <Counter end={1400} prefix="+" />
              </h3>

              <p className="mt-2 text-gray-500 text-[14px] leading-[1.5] max-w-[150px]">
                Pozos intervenidos
              </p>

              <div className="hidden lg:block absolute right-0 top-1/2 -translate-y-1/2 w-[1px] h-[80px] bg-gradient-to-b from-transparent via-gray-300 to-transparent" />
            </div>

            <div className="relative flex flex-col items-center text-center group">
              <div className="w-16 h-16 rounded-2xl flex items-center justify-center transition-all duration-300 group-hover:-translate-y-1 overflow-hidden">
                <img
                  src="/Images/home/operadoras.webp"
                  alt="Operadoras"
                  className="w-14 h-14 object-contain"
                />
              </div>

              <h3 className="mt-5 text-[#111] font-[800] text-[28px] leading-none">
                <Counter end={8} suffix="+" />
              </h3>

              <p className="mt-2 text-gray-500 text-[14px] leading-[1.5] max-w-[150px]">
                Operadoras aliadas
              </p>

              <div className="hidden lg:block absolute right-0 top-1/2 -translate-y-1/2 w-[1px] h-[80px] bg-gradient-to-b from-transparent via-gray-300 to-transparent" />
            </div>

            <div className="flex flex-col items-center text-center group">
              <div className="w-16 h-16 rounded-2xl flex items-center justify-center transition-all duration-300 group-hover:-translate-y-1 overflow-hidden">
                <img
                  src="/Images/home/ideam.webp"
                  alt="IDEAM"
                  className="w-14 h-14 object-contain"
                />
              </div>

              <h3 className="mt-5 text-[#111] font-[800] text-[28px] leading-none">
                IDEAM
              </h3>

              <p className="mt-2 text-gray-500 text-[14px] leading-[1.5] max-w-[150px]">
                Laboratorio acreditado
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CONTACTO CTA */}
      <section className="relative bg-white py-16 overflow-hidden">
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] blur-3xl rounded-full" />
        <div className="relative z-10 max-w-[1100px] mx-auto px-6 lg:px-16">
          <div className="relative bg-white rounded-2xl shadow-[0_20px_60px_rgba(0,0,0,0.10)] px-8 py-12 lg:py-14 flex flex-col lg:flex-row items-center justify-between gap-10 overflow-hidden">
            <div className="absolute left-0 top-0 w-1 h-full bg-gradient-to-b from-[#ee7d32] to-transparent" />

            <div className="flex items-center lg:items-start gap-6 text-center lg:text-left">
              <div className="flex items-center justify-center">
                <Mail
                  size={44}
                  className="stroke-[#ee7d32] drop-shadow-sm"
                  strokeWidth={2}
                />
              </div>

              <div>
                <h2 className="text-[28px] lg:text-[34px] font-[800] text-[#111] leading-tight">
                  Habla con un experto
                </h2>

                <p className="mt-3 text-gray-500 text-[15px] max-w-[520px] leading-relaxed">
                  Escríbenos y recibe asesoría personalizada en soluciones
                  industriales y ambientales.
                </p>
              </div>
            </div>

            <a
              href="/contact-us"
              className="
          relative
          px-10 py-4
          bg-[#ee7d32]
          text-white
          font-semibold
          rounded-xl
          shadow-[0_10px_25px_rgba(243,112,33,0.35)]
          hover:shadow-[0_15px_35px_rgba(243,112,33,0.45)]
          hover:-translate-y-1
          transition-all
          whitespace-nowrap
          overflow-hidden
        "
            >
              <span className="absolute inset-0 bg-white/10 opacity-0 hover:opacity-100 transition" />
              Contáctanos
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
